import { getConditionLabel } from "@features/ax/utils";
import { logEvent } from "@shared/analytics/analytics";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { toast } from "@shared/hooks/use-toast";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Plus } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDeleteFacility, useFacilitiesList } from "../../hooks";
import { FacilityItem } from "../../types";
import { DashboardCards } from "./dashboardCards";
import { FacilityFilter } from "./filter";

const getConditionColor = (score: number | null | undefined): string => {
  if (score === null || score === undefined) return "bg-gray-100 text-gray-800"; // NA
  if (score > 80) return "bg-green-100 text-green-800"; // Good
  if (score >= 40) return "bg-yellow-100 text-yellow-800"; // Fair
  return "bg-red-100 text-red-800"; // Poor
};

const getConditionText = (score: number | null | undefined): string => {
  if (score === null || score === undefined) return "NA";
  if (score > 80) return "Good";
  if (score >= 40) return "Fair";
  return "Poor";
};

const Facilities: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [togglingFacility, setTogglingFacility] = useState<number | null>(null);

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [facilityToDelete, setFacilityToDelete] = useState<FacilityItem | null>(
    null
  );

  // Extract current params from URL - simplified for hook usage
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentFacilityType = searchParams.get("facilityType") || "";
  const currentStatus = searchParams.get("status") || "";

  // Format filters for the hook - similar to your BulkBillTable pattern
  const formatFiltersForHook = () => {
    const hookFilters: Record<string, any> = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    // Add search term
    if (currentSearch.trim()) {
      hookFilters.search = currentSearch.trim();
    }

    // Add filters - only include non-empty values
    if (currentFacilityType && currentFacilityType !== "all") {
      hookFilters.facilityType = currentFacilityType;
    }

    if (currentStatus && currentStatus !== "all") {
      hookFilters.status = Number(currentStatus);
    }

    return hookFilters;
  };
  // Call API with formatted filters - let the hook handle filtering
  const { data: facilitiesData, isLoading } = useFacilitiesList(
    formatFiltersForHook()
  );

  const { mutate: deleteFacilityMutation, isPending: isDeletingFacility } =
    useDeleteFacility();

  // Current filters for FacilityFilter component
  const currentFilters = useMemo(
    () => ({
      search: currentSearch,
      facilityType: currentFacilityType,
      status: currentStatus ? Number(currentStatus) : null,
    }),
    [currentSearch, currentFacilityType, currentStatus]
  );

  // Handle delete click - opens confirmation dialog
  const handleDeleteClick = (facility: FacilityItem) => {
    logEvent("Facility Delete Initiated", { facilityId: facility.id });
    console.log("DELETE DATA", facility);
    setFacilityToDelete(facility);
    setShowDeleteDialog(true);
  };

  useEffect(() => {
    logEvent("Facilities Page Viewed", {});
  }, []);

  // Handle confirmed delete
  const handleConfirmDelete = () => {
    if (facilityToDelete) {
      logEvent("Facility Delete Confirmed", {
        facilityId: facilityToDelete.id,
      });
      deleteFacilityMutation(
        { facilityId: facilityToDelete.id },
        {
          onSuccess: () => {
            toast({
              title: "Success",
              description: `"${facilityToDelete.facilityName}" has been deleted successfully.`,
            });
            setShowDeleteDialog(false);
            setFacilityToDelete(null);
          },
          onError: (error) => {
            toast({
              title: "Delete Failed",
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
    }
  };

  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setFacilityToDelete(null);
  };

  // Define table columns
  const columns: TableColumn<FacilityItem>[] = [
    {
      key: "facilityNo",
      header: "Facility ID",
      type: "custom",
      headerTooltip: "The unique identifier for the facility.",
    },
    {
      key: "facilityName",
      header: "Facility Name",
      type: "custom",
      headerTooltip: "The common name of the facility.",
    },
    {
      key: "facilityTypeDisplay",
      header: "Facility Type",
      headerTooltip: "The primary function or classification of the facility.",
    },
    {
      key: "serviceZoneDisplay",
      header: "Zone",
      headerTooltip:
        "The operational or geographical district this facility belongs to.",
    },
    {
      key: "systemsCount",
      header: "Systems",
      headerTooltip: "The total number of distinct operational systems.",
    },
    {
      key: "assetsCount",
      header: "Assets",
      headerTooltip: "The total number of individual assets registered.",
    },
    {
      key: "conditionScore",
      header: "Condition Score",
      type: "custom",
      headerTooltip: "The overall health score of this facility (0-100).",
      render: (value: number) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          <Badge variant="secondary" className={getConditionLabel(value).color}>
            {getConditionLabel(value).label}
          </Badge>
        </div>
      ),
    },
    {
      key: "overallDesignCapacity",
      header: "Design Capacity",
      width: "w-48",
      headerTooltip: "The facility's design capacity capacity.",
    },
    {
      key: "currentCapacity",
      header: "Current Capacity",
      headerTooltip: "The facility's current operational capacity.",
    },
    {
      key: "statusDisplay",
      header: "Operational Status",
      type: "badge",
      headerTooltip:
        "The current real-time operational status of the facility.",
      render: (value: any, row: FacilityItem) => (
        <Badge variant="secondary" className="font-medium">
          {row.statusDisplay}
        </Badge>
      ),
    },
  ];

  // Table actions with delete confirmation dialog
  const actions: TableAction<FacilityItem>[] = useMemo(
    () => [
      {
        label: "View Details",
        onClick: (item) =>
          navigate(`/ax/asset-registry/facilities/view/${item.id}`),
      },
      {
        label: "Edit",
        onClick: (item) =>
          navigate(`/ax/asset-registry/facilities/edit/${item.id}`),
      },
      {
        label: "Delete",
        onClick: (item) => handleDeleteClick(item),
      },
    ],
    [navigate]
  );

  // Calculate pagination info - now from hook response
  const totalItems = facilitiesData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem,
      endItem,
    }),
    [currentPage, totalPages, totalItems, startItem, endItem]
  );

  // Handle apply filters - similar to BulkBillTable pattern
  const handleApplyFilters = (newFilters: any) => {
    logEvent("Facilities Filters Applied", { filters: newFilters });
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    // Handle single value filters
    if (newFilters.facilityType && newFilters.facilityType !== "all") {
      newParams.facilityType = newFilters.facilityType;
    } else {
      delete newParams.facilityType;
    }

    if (newFilters.status && newFilters.status !== "all") {
      newParams.status = String(newFilters.status);
    } else {
      delete newParams.status;
    }

    if (newFilters.serviceZone && newFilters.serviceZone !== "all") {
      newParams.serviceZone = newFilters.serviceZone;
    } else {
      delete newParams.serviceZone;
    }

    if (newFilters.criticalityLevel && newFilters.criticalityLevel !== "all") {
      newParams.criticalityLevel = newFilters.criticalityLevel;
    } else {
      delete newParams.criticalityLevel;
    }

    if (newFilters.location && newFilters.location !== "all") {
      newParams.location = newFilters.location;
    } else {
      delete newParams.location;
    }

    if (newFilters.dateRange?.from) {
      newParams.startDate = newFilters.dateRange.from;
    } else {
      delete newParams.startDate;
    }

    if (newFilters.dateRange?.to) {
      newParams.endDate = newFilters.dateRange.to;
    } else {
      delete newParams.endDate;
    }

    // Reset to page 1 when filters change
    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    logEvent("Facilities Filters Reset", {});
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    // Clear all filter parameters
    delete newParams.facilityType;
    delete newParams.status;
    delete newParams.serviceZone;
    delete newParams.criticalityLevel;
    delete newParams.location;
    delete newParams.startDate;
    delete newParams.endDate;
    delete newParams.search;

    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  // Handle search - similar to BulkBillTable pattern
  const handleSearch = (searchTerm: string) => {
    logEvent("Facilities Search", { searchTerm });
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = encodeURIComponent(searchTerm.trim());
    } else {
      delete newParams.search;
    }

    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    logEvent("Facilities Page Changed", { page });
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  const hasActiveFilters =
    currentSearch || currentFacilityType || currentStatus;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <PageHeader
            title="Facilities"
            subtitle="Complete list of all facilities and their operational status"
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                logEvent("Facilities Bulk Add Clicked", {});
                navigate("/onboarding/data-migration/data-cards?tab=upload");
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Bulk Add
            </Button>

            <Button
              onClick={() => {
                logEvent("Facilities Add New Facility Clicked", {});
                navigate("/ax/asset-registry/facilities/create");
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Facility
            </Button>
          </div>
        </div>
      </div>

      {/* Dashboard Cards */}
      <DashboardCards />

      <GenericTable
        data={facilitiesData?.results || []}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by Facility ID, Name..."
        searchParamName="search"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        actions={actions}
        // onExport={() => {}}
        actionsType="dropdown"
        filterPanel={
          <FacilityFilter
            currentFilters={currentFilters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
        emptyMessage="No facilities found"
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Filters active
              </span>
            </div>
          ) : null
        }
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        title="Delete Facility"
        itemName={facilityToDelete?.facilityName}
        description={`Are you sure you want to delete "${facilityToDelete?.facilityName}"? This will permanently remove the facility and all its associated systems and assets. This action cannot be undone.`}
        isLoading={isDeletingFacility}
      />
    </div>
  );
};

export default Facilities;
